// Aceleres — memoria do ultimo modulo acessado no Zoraxy.
//
// Ate aqui o app tinha um destino fixo de abertura: quem entrava em "/" (sem
// rota nenhuma na URL) caia sempre em accounts/:id/dashboard, ou seja, em
// Conversas. Para o gestor que trabalha o dia em Carteirizacao ou Integracoes
// isso significava re-navegar toda vez que reabria o navegador.
//
// A memoria é deliberadamente grosseira: guarda o MODULO, nao a pagina. Quem
// fechou o navegador dentro de um contato volta para a lista de contatos, nao
// para o contato — assim nunca caimos numa conversa apagada, transferida ou
// que o usuario perdeu acesso desde ontem.
//
// Onde mora: localStorage, carimbado com o user.id. O carimbo é o que garante
// que o modulo de um usuario nunca vale para outro no mesmo navegador — e ele
// é necessario porque o logout coordenado com a Aceleres
// (Internal::SsoLogoutController) é um redirect server-side onde nenhum JS
// roda, entao nao da para confiar so na limpeza do logout.
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { isAConversationRoute } from './routeHelpers';

// Espelha os itens de topo da Sidebar (components-next/sidebar/Sidebar.vue).
// `entryRoute` é a tela de entrada do modulo; `matches` diz quais rotas contam
// como "estou dentro deste modulo". A ordem importa: vence o primeiro match,
// entao o que é mais especifico vem antes (canned_list é modulo proprio e
// tambem filho de Configuracoes, por isso aparece antes de settings).
export const MODULES = [
  {
    id: 'team_presence',
    entryRoute: 'aceleres_team_presence',
    matches: name => name.startsWith('aceleres_team_presence'),
  },
  {
    id: 'carteirizacao',
    entryRoute: 'aceleres_carteirizacao',
    matches: name => name === 'aceleres_carteirizacao',
  },
  {
    id: 'inbox',
    entryRoute: 'inbox_view',
    matches: name =>
      name === 'inbox_view' || name === 'inbox_view_conversation',
  },
  {
    id: 'notifications',
    entryRoute: 'notifications_index',
    matches: name => name === 'notifications_index',
  },
  {
    id: 'search',
    entryRoute: 'search',
    matches: name => name === 'search',
  },
  {
    id: 'captain',
    entryRoute: 'captain_assistants_index',
    // captain_assistants_index e portals_index exigem :navigationPath. Os
    // valores abaixo sao os mesmos que o redirect das rotas-pai usa.
    entryParams: { navigationPath: 'captain_assistants_responses_index' },
    matches: name => name.startsWith('captain_'),
  },
  {
    id: 'contacts',
    entryRoute: 'contacts_dashboard_index',
    matches: name => name.startsWith('contacts_'),
  },
  {
    id: 'companies',
    entryRoute: 'companies_dashboard_index',
    matches: name => name.startsWith('companies_'),
  },
  {
    id: 'campaigns',
    entryRoute: 'campaigns_livechat_index',
    matches: name => name.startsWith('campaigns_'),
  },
  {
    id: 'canned',
    entryRoute: 'canned_list',
    matches: name => name === 'canned_list',
  },
  {
    id: 'integracoes',
    entryRoute: 'configuracoes_integracoes',
    matches: name => name === 'configuracoes_integracoes',
  },
  {
    id: 'configuracoes',
    entryRoute: 'configuracoes_perfil',
    matches: name => name.startsWith('configuracoes_'),
  },
  {
    id: 'helpcenter',
    entryRoute: 'portals_index',
    entryParams: { navigationPath: 'portals_articles_index' },
    matches: name => name.startsWith('portals_'),
  },
  {
    // A lista de rotas de conversa ja existe em routeHelpers (é a mesma que a
    // sidebar e o chat list usam). Reusar evita que este arquivo divirja dela.
    id: 'conversations',
    entryRoute: 'home',
    matches: name =>
      name === 'account_conversations' ||
      isAConversationRoute(name, true, true),
  },
  // Catch-all das telas de configuracao herdadas do Chatwoot (agent_list,
  // labels_list, settings_inbox_*, sla_*, macros_*, auditlogs_list...). Fica
  // por ultimo de proposito: qualquer rota de settings que nao seja modulo
  // proprio cai aqui, e settings_home ja redireciona sozinha conforme o papel
  // do usuario.
  {
    id: 'settings',
    entryRoute: 'settings_home',
    matches: name =>
      name.startsWith('settings_') ||
      name.startsWith('agent_') ||
      name.startsWith('sla_') ||
      name.startsWith('macros_') ||
      name.startsWith('assignment_policy') ||
      name.startsWith('aurora_') ||
      name.startsWith('profile_settings') ||
      [
        'general_settings_index',
        'security_settings_index',
        'labels_list',
        'attributes_list',
        'automation_list',
        'auditlogs_list',
        'custom_roles_list',
        'team_numbers_list',
        'agent_bots',
        'billing_settings_index',
        'social_channels_settings',
        'conversation_workflow_index',
      ].includes(name),
  },
];

export const findModuleByRouteName = routeName => {
  if (!routeName || typeof routeName !== 'string') return null;
  return MODULES.find(module => module.matches(routeName)) || null;
};

export const getModuleById = moduleId =>
  MODULES.find(module => module.id === moduleId) || null;

/**
 * Grava o modulo da rota visitada. Rota que nao pertence a modulo nenhum nao
 * sobrescreve o registro: uma tela nova ainda nao mapeada nao pode apagar a
 * memoria de quem estava em Carteirizacao.
 */
export const saveLastModule = ({ routeName, accountId, userId } = {}) => {
  if (!userId || !accountId) return;

  const module = findModuleByRouteName(routeName);
  if (!module) return;

  try {
    LocalStorage.set(LOCAL_STORAGE_KEYS.LAST_MODULE, {
      userId: Number(userId),
      accountId: Number(accountId),
      moduleId: module.id,
    });
  } catch (error) {
    // localStorage cheio ou bloqueado: a memoria é conveniencia, nao pode
    // derrubar a navegacao.
  }
};

/**
 * Le o modulo salvo. Devolve null quando o registro é de outro usuario — essa
 * é a garantia de isolamento entre pessoas que dividem o mesmo navegador.
 */
export const readLastModuleId = ({ userId } = {}) => {
  if (!userId) return null;

  let stored = null;
  try {
    stored = LocalStorage.get(LOCAL_STORAGE_KEYS.LAST_MODULE);
  } catch (error) {
    return null;
  }

  if (!stored || typeof stored !== 'object') return null;
  if (Number(stored.userId) !== Number(userId)) return null;

  return getModuleById(stored.moduleId) ? stored.moduleId : null;
};

/**
 * Resolve o caminho de abertura do ultimo modulo. Devolve null (e o chamador
 * mantem o destino padrao) quando nao ha memoria valida ou quando a rota de
 * entrada nao existe mais no router.
 */
export const resolveLastModulePath = ({ router, userId, accountId } = {}) => {
  const moduleId = readLastModuleId({ userId });
  if (!moduleId || !router || !accountId) return null;

  const module = getModuleById(moduleId);
  if (!module) return null;

  try {
    const resolved = router.resolve({
      name: module.entryRoute,
      params: { ...(module.entryParams || {}), accountId: String(accountId) },
    });
    const path = resolved?.fullPath || '';
    // Guarda contra loop de redirect: o galho que chama isto é justamente o de
    // "URL sem rota", entao so devolvemos um caminho que casa com o dashboard.
    return path.startsWith('/app/') ? path : null;
  } catch (error) {
    return null;
  }
};
